import { getAnalytics, isSupported, logEvent, type Analytics } from "firebase/analytics";
import { getFirebaseApp } from "./firebase";
import { measurementId } from "./env";

/**
 * GA4 for the DAC widget funnel.
 *
 * D22: NO PII GOES TO GA4. Not the address, email, phone, name, or ZIP. Google's
 * terms prohibit it and it would put customer PII in a third-party store the
 * privacy policy does not cover.
 *
 * ALLOWLIST, not a denylist, and that inversion is the whole design. A denylist
 * can only reject keys that announce themselves (`email`, `phone`, `zip`); it
 * cannot catch a key carrying PII silently. `lat`/`lng` are far finer geography
 * than the ZIP this bans, and a freeform `q` or `addr` is exactly what a caller
 * reaches for when spreading a state object into an event. So nothing reaches GA4
 * unless its key is named below.
 *
 * `county` is allowed because 62 counties is coarse geography, not identification.
 * `channel` is the literal "tel" or "email" the customer chose, never the number
 * or address they reached.
 *
 * SILENT STRIP: an unlisted key vanishes from the event with no error. If a
 * parameter is missing from GA4 DebugView, check the list before losing an
 * afternoon. (The old denylist had the same trap in reverse: `countyName`
 * disappeared because `name` is a substring.)
 *
 * Never throws, never blocks the UI, and no-ops outside a production build. The
 * `import.meta.env.PROD` check in `initAnalytics`, not the presence of a
 * measurement id, is what keeps dev and PR previews out of the GA4 property.
 */

/**
 * The only params a caller may pass to `track()`. `context` is deliberately
 * excluded from this set: it is computed below and merged into the payload
 * after stripping, so a caller can never override the real render context by
 * spreading a same-named key into `params`.
 */
const CALLER_ALLOWED_KEYS: ReadonlySet<string> = new Set([
  "step",
  "submission_id",
  "is_dac",
  "county",
  "reason",
  "channel",
]);

const context: "embed" | "standalone" =
  typeof window !== "undefined" && new URLSearchParams(window.location.search).has("embed")
    ? "embed"
    : "standalone";

type Primitive = string | number | boolean;
type EventParams = Record<string, Primitive>;

type Logger = (event: string, params: Record<string, unknown>) => void;

let testLogger: Logger | undefined;
/** Test seam. Replaces the GA4 sink so events can be asserted. Never called in production. */
export function __setLoggerForTests(logger: Logger | undefined): void {
  testLogger = logger;
}

let initPromise: Promise<Analytics | null> | null = null;

function initAnalytics(): Promise<Analytics | null> {
  initPromise ??= (async () => {
    if (!import.meta.env.PROD || !measurementId()) return null;
    try {
      if (!(await isSupported())) return null;
      const app = getFirebaseApp();
      return app ? getAnalytics(app) : null;
    } catch {
      return null;
    }
  })();
  return initPromise;
}

function strip(params: EventParams): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (CALLER_ALLOWED_KEYS.has(key)) out[key] = value;
  }
  return out;
}

/** Fire and forget. Always tagged with the render context. */
export function track(event: string, params: EventParams = {}): void {
  const payload = { context, ...strip(params) };

  if (testLogger) {
    try {
      testLogger(event, payload);
    } catch {
      /* analytics must never break the funnel */
    }
    return;
  }

  void initAnalytics()
    .then((analytics) => {
      if (analytics) logEvent(analytics, event, payload);
    })
    .catch(() => {
      /* analytics must never break the funnel */
    });
}
